import React, { useEffect, useState } from 'react';
import './TeamPage.scss';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import BreadCrumbs from '../../components/BreadCrumbs/BreadCrumbs';
import userStore from '../../MobXStorage/UserStore';
import { useGetRoles } from '../../hooks/UserHooks';
import { useService } from 'react-service-locator';
import { SeatsResponse, TeamResponse, TeamService } from '../../services/team.service';
import AddUserModal from './AddUserModal';
import ConfirmRemoveModal from './ConfirmRemoveModal';
import PayInvoiceModal from './PayInvoiceModal';

const TeamPage: React.FC = () => {
    const { t } = useTranslation();
    const teamService = useService(TeamService);

    const [isMasterUser, setIsMasterUser] = useState(false);
    const roles = useGetRoles();

    const [team, setTeam] = useState<TeamResponse | null>(null);
    const [seats, setSeats] = useState<SeatsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [removeUserId, setRemoveUserId] = useState<string | null>(null);
    const [payInvoiceUrl, setPayInvoiceUrl] = useState<string | null>(null);

    useEffect(() => {
        if (roles) setIsMasterUser(roles.includes('MASTER') || roles.includes('ADMIN'));
    }, [roles, userStore.roles]);

    const getErr = (e: any) => e?.response?.data?.error ?? e?.message ?? 'Error';

    const load = async () => {
        setLoading(true);
        try {
            const [teamRes, seatsRes] = await Promise.all([teamService.getTeam(), teamService.getSeats()]);
            setTeam(teamRes);
            setSeats(seatsRes);
        } catch (e: any) {
            toast.error(getErr(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleAddUser = async (data: {
        email: string;
        firstName?: string;
        lastName?: string;
        phoneNumber?: string;
    }) => {
        try {
            const res = await teamService.addUser(data);

            if ((res as any).message === 'UserAdded') {
                toast.success(t('team.addUser.success'));
                await load();
                return;
            }

            // iDEAL path: needs user authorization
            if ((res as any).status === 'RequiresPayment') {
                setPayInvoiceUrl((res as any).hostedInvoiceUrl);
                toast.message(t('team.payInvoice.toast'));
                await load();
                return;
            }

            await load();
        } catch (e: any) {
            const err = getErr(e);
            if (err === 'UserExists') toast.error(t('team.errors.userExists'));
            else toast.error(err);
        }
    };

    const handleRemoveUser = async () => {
        if (!removeUserId) return;
        try {
            await teamService.removeUser(removeUserId);
            toast.success(t('pages.team.removeUser.success'));
            await load();
        } catch (e: any) {
            toast.error(getErr(e));
        } finally {
            setRemoveUserId(null);
        }
    };

    const members = team?.members ?? [];
    members.sort((a, b) => {
        if (a.isMaster) return -1;
        if (b.isMaster) return 1;
        return a.fullName.localeCompare(b.fullName);
    });

    const paidSeats = seats?.seats ?? 0;
    const used = seats?.members ?? members.length;

    return (
        <div id="teamPage" className="teamPage">
            <BreadCrumbs
                breadcrumbs={[
                    { to: '/', name: t('search') },
                    { to: '/team', name: t('components.navbar.team') },
                ]}
            />

            <div className="card">
                <div className="title">
                    {t('components.navbar.team')} ({used} / {paidSeats})
                </div>

                {loading ? (
                    <div>{t('pages.common.loading')}</div>
                ) : (
                    <>
                        <div className="teamMembersList">
                            {members.map((m) => (
                                <div key={m.id} className="teamMemberItem">
                                    <div className="memberInfo">
                                        <h3>{m.fullName || m.email}</h3>
                                        <div>
                                            {m.email}, {m.phoneNumber}
                                        </div>
                                    </div>

                                    {isMasterUser && !m.isMaster && (
                                        <button
                                            className="button button-ready button-team"
                                            onClick={() => setRemoveUserId(m.id)}
                                        >
                                            {t('pages.common.remove')}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        {isMasterUser && (
                            <div className="buttonContainer">
                                <button
                                    className="button button-ready button-team"
                                    onClick={() => setAddModalOpen(true)}
                                >
                                    {t('pages.team.addUser.button')}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            <AddUserModal open={addModalOpen} onClose={() => setAddModalOpen(false)} onSubmit={handleAddUser} />
            <ConfirmRemoveModal
                open={!!removeUserId}
                onClose={() => setRemoveUserId(null)}
                onConfirm={handleRemoveUser}
            />
            <PayInvoiceModal
                open={!!payInvoiceUrl}
                hostedInvoiceUrl={payInvoiceUrl}
                onClose={() => setPayInvoiceUrl(null)}
            />
        </div>
    );
};

export default TeamPage;
